import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import { Mail, Calendar, ShieldCheck, Edit2, Trash2, Eye } from 'lucide-react';

interface UserItem {
  id: string;
  email: string;
  full_name: string | null;
  is_admin: boolean;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
}

interface NewUserForm {
  email: string;
  password: string;
  full_name: string;
  is_admin: boolean;
}

interface EditUserForm {
  full_name: string;
  is_admin: boolean;
  is_active: boolean;
  is_verified: boolean;
}

const emptyNewUserForm: NewUserForm = { email: '', password: '', full_name: '', is_admin: false };
const emptyEditUserForm: EditUserForm = { full_name: '', is_admin: false, is_active: true, is_verified: false };

const AdminUserProfiles: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState<NewUserForm>(emptyNewUserForm);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editUserForm, setEditUserForm] = useState<EditUserForm>(emptyEditUserForm);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const response = await axios.get(API.listAdminUsers(), { headers });
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users list.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openAddModal = () => {
    setNewUserForm(emptyNewUserForm);
    setCreateError('');
    setShowAddModal(true);
  };

  const submitNewUser = async () => {
    setCreating(true);
    setCreateError('');
    const token = localStorage.getItem('access_token');
    try {
      await axios.post(
        API.adminCreateUser(),
        {
          email: newUserForm.email,
          password: newUserForm.password,
          full_name: newUserForm.full_name || undefined,
          is_admin: newUserForm.is_admin,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowAddModal(false);
      setMessage('User created.');
      await fetchUsers();
    } catch (err: any) {
      console.error('Failed to create user:', err);
      setCreateError(err.response?.data?.detail || 'Failed to create user.');
    } finally {
      setCreating(false);
    }
  };

  const openEditModal = (u: UserItem) => {
    setEditingUserId(u.id);
    setEditUserForm({
      full_name: u.full_name || '',
      is_admin: u.is_admin,
      is_active: u.is_active,
      is_verified: u.is_verified,
    });
    setEditError('');
    setShowEditModal(true);
  };

  const submitEditUser = async () => {
    if (!editingUserId) return;
    setSavingEdit(true);
    setEditError('');
    const token = localStorage.getItem('access_token');
    try {
      await axios.put(
        API.adminUpdateUser(editingUserId),
        {
          full_name: editUserForm.full_name || null,
          is_admin: editUserForm.is_admin,
          is_active: editUserForm.is_active,
          is_verified: editUserForm.is_verified,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowEditModal(false);
      setEditingUserId(null);
      setMessage('User updated.');
      await fetchUsers();
    } catch (err: any) {
      console.error('Failed to update user:', err);
      setEditError(err.response?.data?.detail || 'Failed to update user.');
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!showDeleteConfirm) return;
    setDeleting(true);
    const token = localStorage.getItem('access_token');
    try {
      await axios.delete(
        API.adminDeleteUser(showDeleteConfirm),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMessage('User deleted.');
      setShowDeleteConfirm(null);
      await fetchUsers();
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      setError(err.response?.data?.detail || 'Failed to delete user.');
    } finally {
      setDeleting(false);
    }
  };

  if (!user || !user.is_admin) return <div>Access Denied</div>;

  return (
    <div>
      <style>{`
        @media (min-width: 768px) {
          .desktop-table { display: block !important; }
          .mobile-user-cards { display: none !important; }
        }
        @media (max-width: 767px) {
          .desktop-table { display: none !important; }
          .mobile-user-cards { display: block !important; }
        }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>User Management</h1>
        <button className="btn btn-primary" style={{ background: '#DC2626' }} onClick={openAddModal}>Add New User</button>
      </div>

      {(error || message) && (
        <div style={{
          marginBottom: '24px', padding: '16px', borderRadius: '8px', fontSize: '14px',
          background: error ? '#FEF2F2' : '#F0FDF4',
          color: error ? '#991B1B' : '#166534',
          border: `1px solid ${error ? '#FCA5A5' : '#BBF7D0'}`,
        }}>
          {error || message}
        </div>
      )}

      <div className="section-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="desktop-table">
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#F8FAFC', borderBottom: '1px solid var(--sidebar-border)' }}>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>User</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Email</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Joined</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Role</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center' }}>Loading...</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} style={{ borderBottom: '1px solid var(--sidebar-border)' }}>
                <td style={{ padding: '20px 24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '36px', height: '36px', background: '#F1F5F9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B', fontWeight: 600 }}>
                      {u.full_name ? u.full_name.charAt(0) : 'U'}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600 }}>{u.full_name || 'No Name'}</div>
                      <div style={{ fontSize: '12px', color: '#94A3B8' }}>ID: {u.id.split('-')[0]}...</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '20px 24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748B' }}>
                    <Mail size={14} />
                    {u.email}
                  </div>
                </td>
                <td style={{ padding: '20px 24px', color: '#64748B' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Calendar size={14} />
                    {new Date(u.created_at).toLocaleDateString()}
                  </div>
                </td>
                <td style={{ padding: '20px 24px' }}>
                  {u.is_admin ? (
                    <span className="status-pill" style={{ background: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', gap: '4px', width: 'fit-content' }}>
                      <ShieldCheck size={12} /> ADMIN
                    </span>
                  ) : (
                    <span className="status-pill status-outline">USER</span>
                  )}
                </td>
                <td style={{ padding: '20px 24px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      className="btn btn-outline"
                      style={{ padding: '8px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                      onClick={() => navigate(`/admin/users/${u.id}`)}
                    >
                      <Eye size={12} /> View
                    </button>
                    <button
                      className="btn btn-outline"
                      style={{ padding: '8px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                      onClick={() => openEditModal(u)}
                    >
                      <Edit2 size={12} /> Edit
                    </button>
                    {u.id !== user.id && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '8px 12px', fontSize: '12px', color: '#DC2626', borderColor: '#FCA5A5', display: 'flex', alignItems: 'center', gap: '4px' }}
                        onClick={() => setShowDeleteConfirm(u.id)}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {/* mobile card layout */}
        <div className="mobile-user-cards">
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>
          ) : users.map((u) => (
            <div key={u.id} style={{
              padding: '16px',
              borderBottom: '1px solid var(--sidebar-border)',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '36px', height: '36px', background: '#F1F5F9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B', fontWeight: 600, flexShrink: 0 }}>
                  {u.full_name ? u.full_name.charAt(0) : 'U'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>{u.full_name || 'No Name'}</div>
                  <div style={{ fontSize: '13px', color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                </div>
                {u.is_admin ? (
                  <span className="status-pill" style={{ background: '#FEE2E2', color: '#DC2626', flexShrink: 0, fontSize: '11px' }}>
                    <ShieldCheck size={11} /> ADMIN
                  </span>
                ) : (
                  <span className="status-pill status-outline" style={{ flexShrink: 0, fontSize: '11px' }}>USER</span>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#94A3B8', display: 'flex', gap: '16px' }}>
                <span><Calendar size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{new Date(u.created_at).toLocaleDateString()}</span>
                <span>ID: {u.id.split('-')[0]}...</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                <button
                  className="btn btn-outline"
                  style={{ flex: 1, padding: '10px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                  onClick={() => navigate(`/admin/users/${u.id}`)}
                >
                  <Eye size={12} /> View
                </button>
                <button
                  className="btn btn-outline"
                  style={{ flex: 1, padding: '10px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                  onClick={() => openEditModal(u)}
                >
                  <Edit2 size={12} /> Edit
                </button>
                {u.id !== user.id && (
                  <button
                    className="btn btn-outline"
                    style={{ flex: 1, padding: '10px', fontSize: '12px', color: '#DC2626', borderColor: '#FCA5A5', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                    onClick={() => setShowDeleteConfirm(u.id)}
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showAddModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '480px', margin: '20px' }}>
            <h2 className="card-title">Add New User</h2>
            {createError && (
              <div style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px', background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5', fontSize: '13px' }}>
                {createError}
              </div>
            )}
            <div className="form-group">
              <label className="form-label" htmlFor="new-user-email">Email</label>
              <input
                id="new-user-email"
                type="email"
                className="form-input"
                value={newUserForm.email}
                onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="new-user-password">Password</label>
              <input
                id="new-user-password"
                type="password"
                className="form-input"
                value={newUserForm.password}
                onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="new-user-full-name">Full Name</label>
              <input
                id="new-user-full-name"
                className="form-input"
                value={newUserForm.full_name}
                onChange={(e) => setNewUserForm({ ...newUserForm, full_name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newUserForm.is_admin}
                  onChange={(e) => setNewUserForm({ ...newUserForm, is_admin: e.target.checked })}
                />
                Grant admin privileges
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={submitNewUser}
                disabled={creating || !newUserForm.email || !newUserForm.password}
              >
                {creating ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '480px', margin: '20px' }}>
            <h2 className="card-title">Edit User</h2>
            {editError && (
              <div style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px', background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5', fontSize: '13px' }}>
                {editError}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                className="form-input"
                value={editUserForm.full_name}
                onChange={(e) => setEditUserForm({ ...editUserForm, full_name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editUserForm.is_admin}
                  onChange={(e) => setEditUserForm({ ...editUserForm, is_admin: e.target.checked })}
                />
                Admin privileges
              </label>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editUserForm.is_active}
                  onChange={(e) => setEditUserForm({ ...editUserForm, is_active: e.target.checked })}
                />
                Active
              </label>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editUserForm.is_verified}
                  onChange={(e) => setEditUserForm({ ...editUserForm, is_verified: e.target.checked })}
                />
                Verified
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={() => { setShowEditModal(false); setEditingUserId(null); }}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={submitEditUser}
                disabled={savingEdit}
              >
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '400px', margin: '20px', textAlign: 'center' }}>
            <h2 className="card-title" style={{ color: '#DC2626' }}>Delete User</h2>
            <p style={{ margin: '16px 0', color: '#64748B', fontSize: '14px', lineHeight: '1.5' }}>
              Are you sure you want to permanently delete this user?<br />
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={() => setShowDeleteConfirm(null)} disabled={deleting}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ background: '#DC2626' }}
                onClick={confirmDeleteUser}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserProfiles;
